import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  ChevronRight,
  Lock,
  User,
  UserMinus2,
  UserPlus,
  Users,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { View, XStack, YStack } from "tamagui";
import { LoginSheets } from "../core/account/LoginSheets";
import { useApi } from "../core/apis";
import { PlaniraUsps } from "../core/components/PlaniraUsps";
import { useLoadUsersInPlan } from "../core/hooks/useLoadUsersInPlan";
import { useStores } from "../core/stores";
import { trackEvent } from "../core/trackEvent";
import type PlanInfoModuleModel from "../modules/PlanInfoModule/PlanInfoModuleModel";
import { createInfoPropsFromPlan, PlanInfoBox } from "../plans/PlanInfoBox";
import { AccountSheetActive } from "../plans/PlanModel";
import { Block } from "../ui/Block";
import { Body } from "../ui/Body";
import { Box } from "../ui/Box";
import { Button } from "../ui/Button";
import { CenterContent } from "../ui/CenterContent";
import { Group } from "../ui/Group";
import { Input } from "../ui/InputNew";
import { Label } from "../ui/Label";
import { Screen } from "../ui/Screen";
import { Subtext } from "../ui/Subtext";
import { TipsText } from "../ui/TipsText";
import { Title } from "../ui/Title";
import { AvatarSize, UserAvatar } from "../ui/UserAvatar";

type Props = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  "UserSelect"
>;

const PlanInfo: React.FC<{ module: PlanInfoModuleModel }> = observer(
  ({ module }) => {
    const plan = module.plan;

    const { usersStore } = useStores();

    const creatorUser = usersStore.byId.get(plan.creatorUserUid);

    return (
      <>
        <PlanInfoBox info={createInfoPropsFromPlan(plan)} hideEdit />

        {!!creatorUser && (
          <Box>
            <YStack gap="$medium">
              <View>
                <XStack gap="$medium" alignItems="center">
                  <UserAvatar
                    size={AvatarSize.Medium}
                    userUid={plan.creatorUserUid}
                  />
                  <YStack>
                    <Body muted>Created by</Body>
                    <Label>{creatorUser.data.displayName}</Label>
                  </YStack>
                </XStack>
              </View>
            </YStack>
          </Box>
        )}
      </>
    );
  },
);

export const UserSelectUI = observer(({ route, navigation }: Props) => {
  const { planUid, dataUid, moduleUid } = route.params;
  const { plansStore, sectionsStore, modulesStore } = useStores();
  const { plansApi, modulesApi, sectionsApi } = useApi();

  useLoadUsersInPlan(planUid);

  const plan = plansStore.byId.get(planUid);
  const sectionId = plan?.data.sectionIds[0];
  const section = sectionId ? sectionsStore.byId.get(sectionId) : undefined;
  const planInfoModuleId = section?.data.moduleIds[0];
  const planInfoModule = planInfoModuleId
    ? (modulesStore.byId.get(planInfoModuleId) as PlanInfoModuleModel)
    : undefined;

  React.useEffect(() => {
    const unsub = plansApi.watchItem(planUid);
    return () => {
      unsub();
    };
  }, [plansApi, planUid]);

  React.useEffect(() => {
    if (sectionId) {
      const unsub = sectionsApi.watchItem(sectionId);
      return () => {
        unsub();
      };
    }
  }, [sectionsApi, sectionId]);

  React.useEffect(() => {
    if (planInfoModuleId) {
      const unsub = modulesApi.watchItem(planInfoModuleId);
      return () => {
        unsub();
      };
    }
  }, [modulesApi, planInfoModuleId]);

  const { sessionStore } = useStores();

  const [displayName, setDisplayName] = useState(
    sessionStore.superUser?.data.displayName ?? "",
  );
  const hasUsers = !!plan?.users.length;

  const [clickCount, setClickCount] = React.useState(0);
  const [isJoining, setIsJoining] = React.useState(false);

  React.useEffect(() => {
    if (clickCount === 5) {
      plan?.joinWithCurrentUser("Ghost", true);
    }
  }, [plan, clickCount]);

  const joinNow = React.useCallback(() => {
    if (displayName) {
      setIsJoining(true);
      plan?.joinWithCurrentUser(displayName);
      trackEvent("user_select_create_new");
      setIsJoining(false);
    }
  }, [plan, displayName]);

  useEffect(() => {
    if (plan?.userInPlan) {
      if (moduleUid) {
        navigation.replace("Module", {
          dataUid,
          moduleUid,
          planUid,
        });
      } else {
        navigation.replace("Plan", {
          planUid,
        });
      }
    }
  }, [plan?.userInPlan, dataUid, moduleUid, planUid, navigation]);

  const filteredUsers = React.useMemo(() => {
    const planUsers = plan?.users ?? [];

    if (!displayName) {
      return planUsers;
    }

    return planUsers.filter((u) =>
      u.data.displayName.toLowerCase().startsWith(displayName.toLowerCase()),
    );
  }, [plan?.users, displayName]);

  const userExistsAlready = React.useMemo(() => {
    return (
      filteredUsers.find(
        (u) => u.data.displayName.toLowerCase() === displayName.toLowerCase(),
      ) !== undefined
    );
  }, [filteredUsers, displayName]);

  if (!plan?.isUsersLoaded) {
    return null;
  }

  return (
    <Screen skipStickyHeader>
      <YStack gap="$larger">
        <YStack gap="$medium">
          {planInfoModule && <PlanInfo module={planInfoModule} />}

          {plan.isWaitingForApproval && (
            <>
              <CenterContent
                icon={Lock}
                title={"Awaiting approval"}
                subTitle={
                  "You are in the waiting list, and need to get approved to get in."
                }
              />
              <Button
                type="secondary"
                onPress={() => plan.removeUserFromApprovalList()}
                icon={UserMinus2}
              >
                Stop applying
              </Button>
            </>
          )}

          {hasUsers && !plan.isWaitingForApproval && (
            <Box>
              <XStack gap="$medium" alignItems="center">
                <Users />
                <Title>Select user</Title>
              </XStack>
              <Group borderTop={false}>
                {plan?.users.map((user) => {
                  const isInFilter = filteredUsers.find(
                    (u) => u.uid === user.uid,
                  );

                  return (
                    <Block
                      opacity={!isInFilter ? 0.2 : 1}
                      key={user.uid}
                      onPress={() => {
                        if (user.isRegistered) {
                          plan.setShowAccountSheet(AccountSheetActive.Login);
                        } else {
                          user.addSessionUser();
                          trackEvent("user_select_existing");
                        }
                      }}
                      grouped
                      alignItems="center"
                      icon={<UserAvatar userUid={user.uid} />}
                    >
                      <XStack
                        width="100%"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <YStack gap="$space.small" flex={1}>
                          <Body>{user.data.displayName}</Body>
                          {user.isRegistered && (
                            <Subtext>
                              Signed in with email, login required
                            </Subtext>
                          )}
                        </YStack>
                        <ChevronRight />
                      </XStack>
                    </Block>
                  );
                })}
              </Group>
            </Box>
          )}

          {!plan.isWaitingForApproval && (
            <Box>
              <YStack gap="$small">
                <XStack gap="$medium" alignItems="center">
                  <View
                    onPress={() => {
                      setClickCount((c) => c + 1);
                    }}
                  >
                    <User />
                  </View>
                  <Title>
                    Create user and{" "}
                    {plan.data.joinOptions?.needToApply ? "apply" : "join"}?
                  </Title>
                </XStack>
              </YStack>
              {plan.data.settings.isLocked && (
                <CenterContent
                  icon={Lock}
                  title={"Locked for new members"}
                  subTitle={
                    "This Planira is currently locked. No more members can join until it's unlocked."
                  }
                />
              )}
              {plan.isWaitingForApproval && (
                <CenterContent
                  icon={Lock}
                  title={"Awaiting approval"}
                  subTitle={
                    "You are in the waiting list, and need to get approved to get in."
                  }
                />
              )}
              {!plan.data.settings.isLocked && (
                <YStack gap="$medium">
                  {hasUsers && (
                    <Body>
                      Didn't find your name in the list above? Add yourself!
                    </Body>
                  )}
                  <Input
                    placeholder="Your Name or Alias..."
                    defaultValue={displayName}
                    onChangeText={(text) => setDisplayName(text)}
                    onSubmitEditing={joinNow}
                    type="border"
                    error={userExistsAlready}
                    submitOnEnter
                    submitOnBlur={false}
                    clearOnSubmit
                  />

                  {userExistsAlready && (
                    <TipsText text="User exists already, select above to use." />
                  )}

                  {displayName && !filteredUsers.length && (
                    <TipsText text="Great - you can join with your new user" />
                  )}

                  {displayName && (
                    <Button
                      disabled={userExistsAlready || !displayName}
                      isLoading={isJoining}
                      onPress={joinNow}
                      icon={UserPlus}
                    >
                      {plan.data.joinOptions?.needToApply ? "Apply" : "Join"}
                    </Button>
                  )}
                </YStack>
              )}
            </Box>
          )}
        </YStack>

        <PlaniraUsps />
      </YStack>
      <LoginSheets
        currentSheet={plan.showAccountSheet}
        onChangeSheet={(s) => plan.setShowAccountSheet(s)}
      />
    </Screen>
  );
});

export default UserSelectUI;
